'use client';

import { Divider, IconButton } from '@mui/material';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/system';
import { startCase } from 'lodash';
import { useMemo } from 'react';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import { useResponsive } from 'src/hooks/use-responsive';
import { bgGradient } from 'src/theme/css';
import { IOrganization, IPageSetting } from 'src/types/organization';

type Props = {
  bio: IOrganization;
};

interface SmartNavigationProps {
  pageData: IPageSetting;
  onNavClick: (label: string) => void;
}

const SmartNavigation = ({ pageData, onNavClick }: SmartNavigationProps) => {
  const availableNavItems = useMemo(() => {
    const items = [];

    if (pageData) {
      items.push({ label: 'Upcoming', key: 'about' });
    }

    if (pageData?.selectedEvents && pageData?.selectedEvents?.length > 0) {
      items.push({ label: 'Popular', key: 'events' });
    }

    if (pageData?.videoLinks && pageData?.videoLinks?.length > 0) {
      items.push({ label: 'Video', key: 'media' });
    }

    if (pageData?.playlistLinks && pageData?.playlistLinks?.length > 0) {
      items.push({ label: 'Music', key: 'contact' });
    }

    return items;
  }, [pageData]);

  return (
    <Stack direction="row" gap={3} alignItems="center" mt={1.5}>
      {availableNavItems.map((item) => (
        <Typography
          key={item.key}
          color="#A1A1AA"
          component="a"
          className="text-zinc-400 hover:text-zinc-200 transition-colors cursor-pointer"
          onClick={() => onNavClick(item.label)}
        >
          {item.label}
        </Typography>
      ))}
    </Stack>
  );
};

export default function EventDetailsHeader({ bio }: Props) {
  const theme = useTheme();
  const scrollToDiv = (section: string) => {
    const element = document.getElementById(section);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  };
  const mdUp = useResponsive('up', 'md');

  const { logo, pageSetting, name } = bio;

  const handleNavClick = (label: string): void => {
    scrollToDiv(label);
  };

  return (
    <Stack>
      <Stack alignItems="center" justifyContent="center" mt={4}>
        <Box sx={{ width: mdUp ? 400 : 200 }}>
          <Image
            alt="darkmode"
            src={logo?.url}
            sx={{
              borderRadius: 2,
              my: { xs: 2, md: 2 },
              boxShadow: `-40px 40px 80px ${alpha(theme.palette.common.black, 0.24)}`,
            }}
          />
        </Box>
      </Stack>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          ...bgGradient({
            startColor: `${alpha('#09090B', 0.6)} 0%`,
            endColor: `${alpha('#09090B', 1)} 30%`,
          }),
          width: '100%',
          p: 1,
          zIndex: 9,
          mt: mdUp ? 0 : -1,
          minWidth: 'max-content',
          display: 'flex',
          gap: 1,
          backgroundColor: '#09090B',
        }}
      >
        <Typography variant="h3">{startCase(name)}</Typography>
        {pageSetting?.bio && (
          <Typography
            sx={{
              fontFamily: "'Varela Round', sans-serif",
              fontWeight: 400,
              py: 1,
              color: '#c5c5c5',
              whiteSpace: 'pre-line',
            }}
          >
            {pageSetting?.bio}
          </Typography>
        )}
        <Stack direction="row" gap={3} alignItems="center">
          {pageSetting?.showEventsCount && (
            <Stack>
              <Stack direction="row" gap={0.5} alignItems="center" color="#71717A">
                <Iconify icon="mdi:event-available-outline" height={20} width={20} />
                <Typography variant="h6" color="white">
                  {pageSetting?.eventsCount}
                </Typography>
              </Stack>
              <Typography variant="caption" color="#71717A">
                Events
              </Typography>
            </Stack>
          )}
          {pageSetting?.showTicketsCount && (
            <Divider
              orientation="vertical"
              sx={{ borderWidth: '0.01rem', height: 40, backgroundColor: '#71717A' }}
            />
          )}
          {pageSetting?.showTicketsCount && (
            <Stack alignItems="center">
              <Stack direction="row" gap={0.5} alignItems="center" color="#71717A">
                <Iconify icon="f7:tickets" height={20} width={20} />
                <Typography variant="h6" color="white">
                  {pageSetting?.ticketsCount}
                </Typography>
              </Stack>
              <Typography variant="caption" color="#71717A">
                Tickets
              </Typography>
            </Stack>
          )}
          {(pageSetting?.showEventsCount || pageSetting?.showTicketsCount) &&
            pageSetting?.showSubscribersCount && (
              <Divider
                orientation="vertical"
                sx={{ borderWidth: '0.01rem', height: 40, backgroundColor: '#71717A' }}
              />
            )}
          {pageSetting?.showSubscribersCount && (
            <Stack alignItems="center">
              <Stack direction="row" gap={0.5} alignItems="center" color="#71717A">
                <Iconify icon="tabler:users" height={20} width={20} />
                <Typography variant="h6" color="white">
                  {pageSetting?.subscribersCount}
                </Typography>
              </Stack>
              <Typography variant="caption" color="#71717A">
                Subscribers
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack direction="row" gap={3} alignItems="center" mt={1}>
          {pageSetting?.socialMediaLinks &&
            Object.entries(pageSetting.socialMediaLinks).map(([key, value]) => (
              <a
                href={value}
                key={key}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <IconButton
                  sx={{
                    p: 1,
                    '&:hover': { backgroundColor: 'transparent', color: '#FFD369' },
                  }}
                >
                  <Iconify icon={`line-md:${key}`} width={25} height={25} color="white" />
                </IconButton>
              </a>
            ))}
        </Stack>
        {/* {pageSetting && <SmartNavigation pageData={pageSetting} onNavClick={handleNavClick} />} */}
      </Stack>
    </Stack>
  );
}
